import React, { useState } from "react";

import {
  Table,
  Empty,
  Typography,
  Input,
  Button,
  Card,
  Tabs,
  Tag,
  Space,
  Alert,
  Spin,
} from "antd";
import {
  DatabaseOutlined,
  UserOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  KeyOutlined,
  HistoryOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { MyBreadcrumb, handleApiError, handleApiSuccess } from "utils";
import { axiosBase } from "utils";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const checkPasswordColumns = [
  {
    title: "Username",
    dataIndex: "username",
    key: "username",
    render: (v) => <Text strong>{v}</Text>,
  },
  {
    title: "Password",
    dataIndex: "value",
    key: "value",
  },
];

const loginAttemptsColumns = [
  {
    title: "Username",
    dataIndex: "username",
    key: "username",
    render: (v) => <Text strong>{v}</Text>,
  },
  {
    title: "Password",
    dataIndex: "value",
    key: "value",
  },
  {
    title: "Response",
    dataIndex: "reply",
    key: "reply",
    render: (v) => {
      const isOk = v && v.toLowerCase().includes("accept");
      return (
        <Tag color={isOk ? "success" : "error"} style={{ fontWeight: 600 }}>
          {v}
        </Tag>
      );
    },
  },
  {
    title: "Date",
    dataIndex: "authdate",
    key: "authdate",
  },
];

const ipDetailsColumns = [
  {
    title: "Onsite Device",
    dataIndex: "nasipaddress",
    key: "nasipaddress",
  },
  {
    title: "Device MAC & SSID",
    dataIndex: "calledstationid",
    key: "calledstationid",
  },
  {
    title: "Device Type",
    dataIndex: "nasporttype",
    key: "nasporttype",
  },
  {
    title: "Date",
    dataIndex: "acctstarttime",
    key: "acctstarttime",
  },
  {
    title: "User MAC",
    dataIndex: "callingstationid",
    key: "callingstationid",
  },
  {
    title: "Terminate Cause",
    dataIndex: "acctterminatecause",
    key: "acctterminatecause",
  },
  {
    title: "Service Type",
    dataIndex: "servicetype",
    key: "servicetype",
  },
  {
    title: "IP Address",
    dataIndex: "framedipaddress",
    key: "framedipaddress",
    render: (v) =>
      v ? (
        <Tag color="blue" icon={<GlobalOutlined />}>
          {v}
        </Tag>
      ) : (
        "—"
      ),
  },
];

const TAB_DEFS = [
  {
    key: "checkPassword",
    label: "Check Password",
    icon: <KeyOutlined />,
    columns: checkPasswordColumns,
  },
  {
    key: "loginAttempts",
    label: "Login Attempts",
    icon: <HistoryOutlined />,
    columns: loginAttemptsColumns,
  },
  {
    key: "ipDetails",
    label: "IP Details",
    icon: <GlobalOutlined />,
    columns: ipDetailsColumns,
  },
];

export default function RaddbReports() {
  const axios = axiosBase.create();
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchNote, setMatchNote] = useState("");
  const [activeTab, setActiveTab] = useState("checkPassword");
  const [username, setUsername] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);

  const loadReport = (type, uname) => {
    setLoading(true);
    setItems([]);
    setMatches([]);
    setMatchNote("");
    axios({
      method: "GET",
      url: `/raddb-reports?type=${encodeURIComponent(type)}&username=${encodeURIComponent(uname)}`,
    })
      .then((response) => {
        const found = Array.isArray(response.data.matches)
          ? response.data.matches
          : [];
        if (found.length > 0) {
          setMatches(found);
          setMatchNote(response.data.message || "");
          setItems([]);
          return;
        }
        handleApiSuccess(response);
        setItems(response.data.items || []);
      })
      .catch((error) => {
        setItems([]);
        setMatches([]);
        handleApiError(error);
      })
      .finally(() => setLoading(false));
  };

  const chooseMatch = (name) => {
    setInputValue(name);
    setUsername(name);
    loadReport(activeTab, name);
  };

  const handleSearch = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setUsername(trimmed);
    setActiveTab("checkPassword");
    loadReport("checkPassword", trimmed);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (username) {
      loadReport(key, username);
    }
  };

  const handleClear = () => {
    setUsername("");
    setInputValue("");
    setItems([]);
    setMatches([]);
    setMatchNote("");
    setActiveTab("checkPassword");
  };

  const activeTabDef = TAB_DEFS.find((t) => t.key === activeTab);

  return (
    <>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Login Attempt Information", path: "" },
        ]}
      />

      <div style={{ paddingBottom: 24 }}>
        {/* ── Username lookup card ────────────────────────────── */}
        <Card
          style={{
            marginBottom: 16,
            borderRadius: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,.07)",
            border: "1px solid #e8eaf0",
          }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Space align="center">
              {username && (
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  onClick={handleClear}
                  size="small"
                  style={{ color: "#999", marginLeft: -4 }}
                />
              )}
              <Title level={4} style={{ margin: 0 }}>
                Login Attempt Information
              </Title>
            </Space>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Enter a full Wi-Fi username, or part of one. Letter case does not
              matter.
            </Text>
            <Space.Compact style={{ width: "100%", maxWidth: 460 }}>
              <Input
                size="large"
                prefix={<UserOutlined style={{ color: "#bbb" }} />}
                placeholder="Username or part of it…"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
                style={{ borderRadius: "8px 0 0 8px" }}
              />
              <Button
                size="large"
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                disabled={!inputValue.trim()}
                style={{
                  borderRadius: "0 8px 8px 0",
                  paddingLeft: 20,
                  paddingRight: 20,
                }}
              >
                Look Up
              </Button>
            </Space.Compact>
          </Space>
        </Card>

        {/* ── Active-query banner ──────────────────────────────── */}
        {username && (
          <Alert
            style={{
              marginBottom: 20,
              borderRadius: 8,
              border: "1px solid #d6e4ff",
              background: "#f0f5ff",
            }}
            message={
              <Space>
                <DatabaseOutlined style={{ color: "#2f54eb" }} />
                <Text style={{ color: "#2f54eb" }}>
                  Querying records for username:{" "}
                  <Text strong style={{ color: "#1d39c4", fontSize: 15 }}>
                    {username}
                  </Text>
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={handleClear}
                  style={{ color: "#aaa", marginLeft: 8 }}
                >
                  Clear
                </Button>
              </Space>
            }
            type="info"
            showIcon={false}
          />
        )}

        {/* ── Tabs + results ──────────────────────────────────── */}
        {username ? (
          <Card
            style={{
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,.07)",
              border: "1px solid #e8eaf0",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              size="large"
              tabBarStyle={{
                margin: 0,
                paddingLeft: 16,
                paddingRight: 16,
                borderBottom: "1px solid #f0f0f0",
                background: "#fafafa",
                borderRadius: "10px 10px 0 0",
              }}
            >
              {TAB_DEFS.map((tab) => (
                <TabPane
                  key={tab.key}
                  tab={
                    <Space>
                      {tab.icon}
                      {tab.label}
                    </Space>
                  }
                >
                  <div style={{ padding: "20px 16px" }}>
                    <Spin spinning={loading} tip="Loading records…">
                      {matches.length > 0 ? (
                        <div>
                          <Text>{matchNote}</Text>
                          <div
                            style={{
                              marginTop: 12,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {matches.map((name) => (
                              <Button key={name} onClick={() => chooseMatch(name)}>
                                {name}
                              </Button>
                            ))}
                          </div>
                        </div>
                      ) : !loading && items.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <Text type="secondary">
                              No records found for{" "}
                              <strong>{username}</strong> in{" "}
                              <strong>{tab.label}</strong>
                            </Text>
                          }
                          style={{ padding: "40px 0" }}
                        />
                      ) : (
                        <Table
                          dataSource={items}
                          columns={activeTabDef ? activeTabDef.columns : []}
                          rowKey={(r, i) => i}
                          pagination={{ pageSize: 15, showSizeChanger: true }}
                          size="middle"
                          bordered
                          style={{ borderRadius: 8 }}
                        />
                      )}
                    </Spin>
                  </div>
                </TabPane>
              ))}
            </Tabs>
          </Card>
        ) : (
          /* ── Placeholder before username is entered ─────────── */
          <Card
            style={{
              borderRadius: 10,
              border: "1px dashed #d9d9d9",
              background: "#fafafa",
              textAlign: "center",
              padding: "60px 0",
            }}
            bodyStyle={{ padding: "40px 24px" }}
          >
            <Space direction="vertical" size={12} align="center">
              <DatabaseOutlined
                style={{ fontSize: 48, color: "#d9d9d9" }}
              />
              <Title level={4} style={{ color: "#bbb", fontWeight: 400, margin: 0 }}>
                Enter a username above to get started
              </Title>
              <Text type="secondary">
                Check password, login attempts, and IP details will appear here
              </Text>
            </Space>
          </Card>
        )}
      </div>
    </>
  );
}
